import React, { useCallback, useEffect, useState } from "react";
import {
    Box, Typography, CircularProgress, Alert, Chip, Button, TextField,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";

import { LOYALTY_API_BASE } from "../../constants";
import { loyaltyFetch } from "../../hooks/loyaltyFetch";
import { loyaltyApiDetail } from "../../utils/loyaltyApiMessage";
import {
    buildCustomerPath,
    loyaltyBrandHeaders,
} from "../../utils/loyaltyCustomerApi";

function historyItemKey(entry, idx) {
    return entry?.id || entry?.event_id || `${entry?.created_at || entry?.occurred_at || idx}`;
}

function formatWhen(entry) {
    const raw = entry?.created_at || entry?.occurred_at || entry?.timestamp;
    return raw ? new Date(raw).toLocaleString("fr-FR") : "–";
}

function entryTitle(entry) {
    return (
        entry?.title
        || entry?.label
        || entry?.event_type
        || entry?.action
        || entry?.type
        || "Événement"
    );
}

function entryDescription(entry) {
    if (entry?.description) return entry.description;
    if (entry?.summary) return entry.summary;
    if (entry?.message) return entry.message;
    const parts = [];
    if (entry?.resource_type) parts.push(entry.resource_type);
    if (entry?.resource_name) parts.push(`« ${entry.resource_name} »`);
    if (entry?.status_from && entry?.status_to) {
        parts.push(`${entry.status_from} → ${entry.status_to}`);
    }
    return parts.length > 0 ? parts.join(" · ") : null;
}

/**
 * Timeline historique entitlements (client ou admin global).
 * @param {"client"|"admin"} mode
 */
const EntitlementsHistoryPanel = ({
    mode = "client",
    brandName,
    profileId,
    email = "",
    showProfileFilter = false,
    limit = 50,
}) => {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(null);
    const [offset, setOffset] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [profileFilter, setProfileFilter] = useState("");

    const fetchHistory = useCallback(async (nextOffset = 0, append = false) => {
        if (!brandName) return;
        if (mode === "client" && !profileId) return;

        const emailNorm = String(email || "").trim();
        if (mode === "client" && !emailNorm) {
            setError("Email requis pour charger l'historique (réconciliation après fusion Unomi).");
            setItems([]);
            return;
        }

        setLoading(true);
        setError(null);
        const headers = loyaltyBrandHeaders(brandName);
        const params = new URLSearchParams({ limit: String(limit), offset: String(nextOffset) });
        if (mode === "admin" && profileFilter.trim()) {
            params.set("profile_id", profileFilter.trim());
        }
        const url = mode === "client"
            ? `${buildCustomerPath(brandName, profileId, "/entitlements/history", emailNorm)}&${params}`
            : `${LOYALTY_API_BASE}/admin/entitlements/history?${params}`;

        try {
            const res = await loyaltyFetch(url, { headers });
            const data = await res.json().catch(() => null);
            if (!res.ok) throw new Error(loyaltyApiDetail(data?.detail) || `Erreur HTTP ${res.status}`);
            const nextItems = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
            setItems((prev) => (append ? [...prev, ...nextItems] : nextItems));
            setTotal(Number.isFinite(data?.total) ? data.total : null);
            setOffset(nextOffset);
        } catch (err) {
            setError(err.message);
            if (!append) setItems([]);
        } finally {
            setLoading(false);
        }
    }, [brandName, profileId, email, mode, limit, profileFilter]);

    useEffect(() => {
        setItems([]);
        setOffset(0);
        fetchHistory(0, false);
    }, [fetchHistory]);

    const hasMore = total != null ? items.length < total : items.length >= limit;

    return (
        <Box>
            {showProfileFilter && (
                <Box display="flex" gap={1} mb={2} flexWrap="wrap" alignItems="center">
                    <TextField
                        size="small"
                        label="Filtrer par profile_id"
                        value={profileFilter}
                        onChange={(e) => setProfileFilter(e.target.value)}
                        sx={{ minWidth: 220 }}
                    />
                    <Button size="small" variant="outlined" onClick={() => fetchHistory(0, false)}
                        sx={{ textTransform: "none", borderRadius: 2 }}>
                        Appliquer
                    </Button>
                </Box>
            )}

            {loading && items.length === 0 && (
                <Box display="flex" justifyContent="center" py={3}>
                    <CircularProgress size={28} />
                </Box>
            )}

            {error && (
                <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>{error}</Alert>
            )}

            {!loading && !error && items.length === 0 && (
                <Typography fontSize={13} color="text.secondary" fontStyle="italic" py={2}>
                    Aucun événement dans l'historique des droits.
                </Typography>
            )}

            {items.length > 0 && (
                <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
                    {items.map((entry, idx) => {
                        const desc = entryDescription(entry);
                        return (
                            <Box
                                component="li"
                                key={historyItemKey(entry, idx)}
                                sx={{
                                    mb: 1.5, pl: 2, borderLeft: "3px solid #e2e8f0",
                                    pb: 1.5,
                                }}
                            >
                                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={0.25}>
                                    <HistoryIcon sx={{ fontSize: 14, color: "#94a3b8" }} />
                                    <Typography fontSize={11} color="text.secondary" fontWeight={600}>
                                        {formatWhen(entry)}
                                    </Typography>
                                    {entry?.event_type && (
                                        <Chip label={entry.event_type} size="small" variant="outlined"
                                            sx={{ fontSize: 10, height: 20, fontWeight: 600 }} />
                                    )}
                                    {mode === "admin" && entry?.profile_id && (
                                        <Chip label={entry.profile_id} size="small"
                                            sx={{ fontSize: 10, height: 20, bgcolor: "#f1f5f9" }} />
                                    )}
                                </Box>
                                <Typography fontSize={13} fontWeight={700} color="#1e293b">
                                    {entryTitle(entry)}
                                </Typography>
                                {desc && (
                                    <Typography fontSize={12} color="#475569" mt={0.25}>{desc}</Typography>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            )}

            {hasMore && items.length > 0 && (
                <Box mt={1}>
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={loading}
                        onClick={() => fetchHistory(offset + limit, true)}
                        sx={{ textTransform: "none", borderRadius: 2 }}
                    >
                        {loading ? "Chargement…" : "Charger plus"}
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default EntitlementsHistoryPanel;
